/**
 * BarcodePrintDialog.jsx - Barcode Label Printing via BarTender Bridge
 * 
 * TODO: REFACTORING NEEDED (Code Quality Review)
 * This component has grown to 600+ lines and should be split into:
 * - PrintPreview: Label preview and layout visualization
 * - PrintSettings: Bridge config (paths, printer name)
 * - LabelConfiguration: Label count, set calculations
 * - BridgeStatus: Connection status chip and diagnostics
 * 
 * For now, the code is organized into logical sections.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Trash2, Printer, X, Crosshair, Plug, AlertCircle, Wrench, Save,
  Package, LayoutGrid, AlertTriangle, Download, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { normalizeProduct } from "@/utils/barcodePrintUtils";
import {
  checkBridge,
  sendPrint,
  getBridgeConfig,
  setBridgeConfig,
} from "@/lib/printBridge";
import { useAuth } from "@/lib/auth";

const PRINTER_MODEL = "XP-TT426B";
const LABELS_PER_SET = 5;
const DEFAULT_TEMPLATE_PATH = "D:\\hitman.btw";

/** Extract just the filename (no folder) from a Windows or POSIX-style path. */
function templateBasename(p) {
  if (!p) return "";
  const parts = String(p).split(/[\\/]/);
  return parts[parts.length - 1] || "";
}

// 5-corner test pattern — used by "Test Print" to verify alignment without
// wasting actual product labels.
const TEST_PATTERN = [
  { product_id: "test-TL", auto_barcode: "TL", mrp: "TL", default_price: "TL", quantity: 1, product_name: "Test TL" },
  { product_id: "test-TR", auto_barcode: "TR", mrp: "TR", default_price: "TR", quantity: 1, product_name: "Test TR" },
  { product_id: "test-C",  auto_barcode: "C",  mrp: "C",  default_price: "C",  quantity: 1, product_name: "Test C"  },
  { product_id: "test-BL", auto_barcode: "BL", mrp: "BL", default_price: "BL", quantity: 1, product_name: "Test BL" },
  { product_id: "test-BR", auto_barcode: "BR", mrp: "BR", default_price: "BR", quantity: 1, product_name: "Test BR" },
];

/**
 * BarcodePrintDialog — configuration modal for printing barcode labels via
 * the local Print Bridge → BarTender → Xprinter XP-TT426B.
 *
 * BarTender owns the visual layout (template configured on the bridge,
 * default `D:\hitman.btw`). This dialog therefore shows NO visual preview
 * and NO calibration controls — only:
 *   - editable product list (Product / Barcode / MRP / DP / Qty)
 *   - Print Summary card (counts of full/partial sets)
 *   - Bridge status + settings
 *   - Test Print / Cancel / Print actions
 */
export default function BarcodePrintDialog({
  open,
  onOpenChange,
  sourceProducts = [],
  defaultQuantityKey = "quantity",
  title = "Print Barcode Labels",
}) {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [rows, setRows] = useState([]);

  // ----- Print Bridge state -----
  const [bridgeStatus, setBridgeStatus] = useState({ checked: false, ok: false, info: null });
  const [printing, setPrinting] = useState(false);
  const [bridgeConfigOpen, setBridgeConfigOpen] = useState(false);
  const [bridgeConfig, setBridgeConfigState] = useState({
    bartender_path: "",
    btw_path: "",
    printer_name: "",
  });
  const [savingBridgeConfig, setSavingBridgeConfig] = useState(false);

  // Reset rows when dialog opens or sources change.
  useEffect(() => {
    if (!open) return;
    const initial = (sourceProducts || [])
      .map((raw) => {
        const qty = raw?.[defaultQuantityKey] ?? raw?.quantity ?? 1;
        return normalizeProduct(raw, qty);
      })
      .filter(Boolean);
    setRows(initial);
  }, [open, sourceProducts, defaultQuantityKey]);

  // Probe bridge + load its config when dialog opens.
  const probeBridge = async () => {
    try {
      const info = await checkBridge();
      setBridgeStatus({ checked: true, ok: !!info?.ok, info });
      return info;
    } catch (e) {
      setBridgeStatus({ checked: true, ok: false, info: null, error: e.message });
      return null;
    }
  };

  useEffect(() => {
    if (!open) return;
    probeBridge().then(async (info) => {
      if (!info) return;
      try {
        const cfg = await getBridgeConfig();
        setBridgeConfigState({
          bartender_path: cfg.bartender_path || "",
          btw_path: cfg.btw_path || "",
          printer_name: cfg.printer_name || "",
        });
      } catch (_) { /* ignore */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Helper to sanitize paths - removes invisible Unicode characters
  // that can get copied from Windows File Explorer (LTR/RTL embedding, zero-width chars, etc.)
  const sanitizePath = (p) => {
    if (!p || typeof p !== "string") return p;
    return p
      .replace(/[\u200B-\u200D\u2028-\u202F\uFEFF\u00A0]/g, "") // Zero-width, directional, BOM, NBSP
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, "") // Control characters
      .trim();
  };

  const saveBridgeConfig = async () => {
    setSavingBridgeConfig(true);
    try {
      // Sanitize paths before sending to bridge
      const cleanConfig = {
        bartender_path: sanitizePath(bridgeConfig.bartender_path),
        btw_path: sanitizePath(bridgeConfig.btw_path),
        printer_name: bridgeConfig.printer_name,
      };
      const next = await setBridgeConfig(cleanConfig);
      setBridgeConfigState({
        bartender_path: next.bartender_path || "",
        btw_path: next.btw_path || "",
        printer_name: next.printer_name || "",
      });
      const healthInfo = await probeBridge();
      
      // Show detailed feedback based on health response
      if (healthInfo?.bartender_present && healthInfo?.btw_present) {
        toast.success("Bridge settings saved and verified! Ready to print.");
      } else if (!healthInfo?.bartender_present) {
        toast.warning(`Config saved, but BarTender exe not found at: ${next.bartender_path}. Check if the file exists.`);
      } else if (!healthInfo?.btw_present) {
        toast.warning(`Config saved, but template not found at: ${next.btw_path}. Check if the file exists.`);
      } else {
        toast.success("Bridge settings saved");
      }
    } catch (e) {
      console.error("[Bridge] Save error:", e);
      toast.error(e?.message || "Could not save bridge settings");
    } finally {
      setSavingBridgeConfig(false);
    }
  };

  // ----- File Browser for BarTender EXE and BTW Template -----
  // Note: Browser security prevents us from getting the full file path.
  // These browse buttons open a prompt for the user to enter the full path.
  const browseForBarTenderExe = async () => {
    const currentPath = bridgeConfig.bartender_path || "";
    const userPath = window.prompt(
      "Enter the full path to BarTender executable (bartend.exe):\n\nExample: D:\\BarTender\\BarTend.exe",
      currentPath || "D:\\BarTender\\BarTend.exe"
    );
    if (userPath !== null && userPath.trim()) {
      setBridgeConfigState((c) => ({ ...c, bartender_path: userPath.trim() }));
      toast.success("Path updated. Click 'Save' to apply.");
    }
  };

  const browseForBtwTemplate = async () => {
    const currentPath = bridgeConfig.btw_path || "";
    const userPath = window.prompt(
      "Enter the full path to the .btw template file:\n\nExample: D:\\hitman.btw",
      currentPath || DEFAULT_TEMPLATE_PATH
    );
    if (userPath !== null && userPath.trim()) {
      setBridgeConfigState((c) => ({ ...c, btw_path: userPath.trim() }));
      toast.success("Path updated. Click 'Save' to apply.");
    }
  };

  // ----- Derived counts -----
  const printableRows = useMemo(
    () => rows.filter((r) => Number(r.quantity) > 0),
    [rows],
  );
  const totalLabels = useMemo(
    () => printableRows.reduce((acc, r) => acc + (parseInt(r.quantity, 10) || 0), 0),
    [printableRows],
  );
  const productsSelected = printableRows.length;
  const fullSets = Math.floor(totalLabels / LABELS_PER_SET);
  const partialLabels = totalLabels % LABELS_PER_SET;
  const totalSetsRequired = Math.ceil(totalLabels / LABELS_PER_SET);

  // Template path comes from the Print Bridge (/health + /config). Default is
  // D:\hitman.btw — the bridge enforces this on the server side. We just
  // render it in the UI / toasts / placeholders so nothing is hardcoded here.
  const templatePath =
    bridgeStatus.info?.btw_path ||
    bridgeConfig.btw_path ||
    DEFAULT_TEMPLATE_PATH;
  const templateName = templateBasename(templatePath) || templateBasename(DEFAULT_TEMPLATE_PATH);
  const templateHeader = `${templateName.toUpperCase()} TEMPLATE`;

  // ----- Row editing -----
  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const handleQtyChange = (idx, raw) => {
    if (raw === "") { updateRow(idx, { quantity: "" }); return; }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error("Quantity must be a positive integer"); return; }
    updateRow(idx, { quantity: n });
  };

  // ----- Bridge → BarTender print flow -----
  const sendToBartender = async (productsToPrint) => {
    const info = await probeBridge();
    if (!info?.ok) {
      toast.error("Print Bridge not detected. Start the bridge app on this PC.");
      return;
    }
    if (info.bartender_present === false) {
      toast.error("BarTender exe not found. Click the Wrench icon to configure paths.");
      setBridgeConfigOpen(true);
      return;
    }
    if (info.btw_present === false) {
      toast.error(`${templateName} template not found at ${templatePath}. Click the Wrench icon to configure paths.`);
      setBridgeConfigOpen(true);
      return;
    }

    // Expand quantity → one CSV row per physical label (matches .btw data binding).
    const flat = [];
    for (const r of productsToPrint) {
      const q = Math.max(1, parseInt(r.quantity, 10) || 1);
      for (let i = 0; i < q; i++) {
        flat.push({
          barcode: String(r.auto_barcode || ""),
          mrp: r.mrp ?? "",
          dp: r.default_price ?? "",
        });
      }
    }
    if (flat.length === 0) {
      toast.error("Nothing to print.");
      return;
    }

    setPrinting(true);
    try {
      const resp = await sendPrint(flat);
      toast.success(`Sent ${resp.rows} label${resp.rows === 1 ? "" : "s"} to BarTender`);
    } catch (e) {
      const detail = e?.data?.stderr ? `\n${e.data.stderr}` : "";
      toast.error((e?.message || "Print failed") + detail);
    } finally {
      setPrinting(false);
    }
  };

  const handlePrint = () => {
    if (printableRows.length === 0) {
      toast.error("Add at least one product with quantity ≥ 1");
      return;
    }
    if (totalLabels > 500) {
      const ok = window.confirm(`You're about to print ${totalLabels} labels. Continue?`);
      if (!ok) return;
    }
    sendToBartender(printableRows);
  };

  const handleTestPrint = () => sendToBartender(TEST_PATTERN);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="barcode-print-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Printer className="w-4 h-4" /> {title}
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-1">
            Xprinter {PRINTER_MODEL} · BarTender via local Print Bridge · <span data-testid="bc-template-header">{templateHeader}</span>
          </div>
        </DialogHeader>

        <div className="flex-1 grid lg:grid-cols-[1.4fr_1fr] overflow-hidden">
          {/* ============ LEFT: editable rows ============ */}
          <div className="border-r border-border overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
              Products ({rows.length}) · Total labels:{" "}
              <span className="font-mono-data font-semibold text-foreground" data-testid="bc-total-labels">
                {totalLabels}
              </span>
            </div>
            <ScrollArea className="flex-1 px-2">
              <table className="w-full text-xs" data-testid="bc-rows-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="p-2 font-medium">Product</th>
                    <th className="p-2 font-medium">Barcode</th>
                    <th className="p-2 font-medium text-right w-[80px]">MRP</th>
                    <th className="p-2 font-medium text-right w-[80px]">DP</th>
                    <th className="p-2 font-medium text-right w-[70px]">Qty</th>
                    <th className="p-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground" data-testid="bc-empty-rows">
                        No products selected.
                      </td>
                    </tr>
                  )}
                  {rows.map((r, idx) => (
                    <tr
                      key={`${r.product_id || r.auto_barcode}-${idx}`}
                      className="border-t border-border align-middle"
                      data-testid={`bc-row-${idx}`}
                    >
                      <td className="p-2 max-w-[220px]">
                        <div className="truncate font-medium" title={r.product_name}>
                          {r.product_name || "—"}
                        </div>
                      </td>
                      <td className="p-2 font-mono-data text-muted-foreground">{r.auto_barcode || "—"}</td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" step="0.01" min={0}
                          value={r.mrp ?? ""}
                          onChange={(e) => updateRow(idx, { mrp: e.target.value === "" ? "" : Number(e.target.value) })}
                          className="h-7 text-xs text-right font-mono-data"
                          data-testid={`bc-mrp-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" step="0.01" min={0}
                          value={r.default_price ?? ""}
                          onChange={(e) => updateRow(idx, { default_price: e.target.value === "" ? "" : Number(e.target.value) })}
                          className="h-7 text-xs text-right font-mono-data"
                          data-testid={`bc-dp-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" min={0} step={1}
                          value={r.quantity}
                          onChange={(e) => handleQtyChange(idx, e.target.value)}
                          className="h-7 text-xs text-right font-mono-data"
                          data-testid={`bc-qty-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => removeRow(idx)}
                          className="h-7 w-7 p-0 text-destructive"
                          data-testid={`bc-remove-${idx}`}
                          title="Remove from print list"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          </div>

          {/* ============ RIGHT: Print Summary card ============ */}
          <div className="bg-[#f4f4f5] overflow-hidden flex flex-col" data-testid="bc-summary-pane">
            <div className="px-5 py-3 border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <LayoutGrid className="w-3.5 h-3.5" /> Print Summary
            </div>
            <ScrollArea className="flex-1">
              <Card className="m-3 p-4 bg-white" data-testid="bc-print-summary">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <SummaryCell label="Products Selected" value={productsSelected} testid="bc-summary-products" />
                  <SummaryCell label="Total Labels" value={totalLabels} testid="bc-summary-total-labels" emphasis />
                  <SummaryCell label="Labels per Set" value={LABELS_PER_SET} testid="bc-summary-labels-per-set" muted />
                  <SummaryCell label="Full Sets" value={fullSets} testid="bc-summary-full-sets" />
                  <SummaryCell label="Partial Labels" value={partialLabels} testid="bc-summary-partial" />
                  <SummaryCell label="Total Sets Required" value={totalSetsRequired} testid="bc-summary-sets-required" emphasis />
                </div>

                {partialLabels > 0 && (
                  <div
                    className="mt-4 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    data-testid="bc-partial-note"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      The final set has only <strong>{partialLabels}</strong> label
                      {partialLabels === 1 ? "" : "s"} — the remaining{" "}
                      <strong>{LABELS_PER_SET - partialLabels}</strong> label
                      {LABELS_PER_SET - partialLabels === 1 ? "" : "s"} in that
                      section may remain unused.
                    </span>
                  </div>
                )}

                {totalLabels > 0 && (
                  <div
                    className="mt-3 text-[11px] text-muted-foreground flex items-start gap-2"
                    data-testid="bc-load-hint"
                  >
                    <Package className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Load at least <strong>{totalSetsRequired}</strong> label section
                      {totalSetsRequired === 1 ? "" : "s"} into the printer.
                    </span>
                  </div>
                )}

                {totalLabels === 0 && (
                  <div className="mt-2 text-[11px] text-muted-foreground" data-testid="bc-summary-empty">
                    Set a quantity ≥ 1 on at least one product to print.
                  </div>
                )}
              </Card>
            </ScrollArea>
          </div>
        </div>

        {/* ============ FOOTER ============ */}
        <div className="border-t bg-muted/20">
          {/* Bridge-settings panel (BarTender exe / btw / printer) */}
          {bridgeConfigOpen && (
            <div className="px-6 py-3 border-b bg-amber-50/40" data-testid="bc-bridge-config">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Wrench className="w-3.5 h-3.5" /> Print Bridge Configuration
                </span>
                <Button variant="ghost" size="sm" onClick={() => setBridgeConfigOpen(false)} className="h-6 text-xs">
                  <X className="w-3 h-3 mr-1" /> Close
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5 flex items-center gap-1">
                    BarTender exe path
                    {bridgeStatus.info?.bartender_present === true && <span className="text-emerald-600" title="File found">✓</span>}
                    {bridgeStatus.info?.bartender_present === false && <span className="text-red-600" title="File NOT found">✗</span>}
                  </label>
                  <div className="flex gap-1">
                    <Input
                      value={bridgeConfig.bartender_path}
                      onChange={(e) => setBridgeConfigState((c) => ({ ...c, bartender_path: sanitizePath(e.target.value) }))}
                      placeholder="D:\BarTender\BarTend.exe"
                      className={`h-8 text-xs font-mono-data flex-1 ${bridgeStatus.info?.bartender_present === false ? 'border-red-400' : ''}`}
                      data-testid="bc-bridge-bartender-path"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 shrink-0"
                      onClick={browseForBarTenderExe}
                      title="Edit path"
                      data-testid="bc-browse-bartender"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {bridgeStatus.info?.bartender_present === false && bridgeStatus.info?.bartender_path && (
                    <p className="text-[9px] text-red-600 mt-0.5">Bridge checking: {bridgeStatus.info.bartender_path}</p>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5 flex items-center gap-1">
                    .btw template path
                    {bridgeStatus.info?.btw_present === true && <span className="text-emerald-600" title="File found">✓</span>}
                    {bridgeStatus.info?.btw_present === false && <span className="text-red-600" title="File NOT found">✗</span>}
                  </label>
                  <div className="flex gap-1">
                    <Input
                      value={bridgeConfig.btw_path}
                      onChange={(e) => setBridgeConfigState((c) => ({ ...c, btw_path: sanitizePath(e.target.value) }))}
                      placeholder={DEFAULT_TEMPLATE_PATH}
                      className={`h-8 text-xs font-mono-data flex-1 ${bridgeStatus.info?.btw_present === false ? 'border-red-400' : ''}`}
                      data-testid="bc-bridge-btw-path"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 shrink-0"
                      onClick={browseForBtwTemplate}
                      title="Edit path"
                      data-testid="bc-browse-btw"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {bridgeStatus.info?.btw_present === false && bridgeStatus.info?.btw_path && (
                    <p className="text-[9px] text-red-600 mt-0.5">Bridge checking: {bridgeStatus.info.btw_path}</p>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">Printer name (optional)</label>
                  <Input
                    value={bridgeConfig.printer_name}
                    onChange={(e) => setBridgeConfigState((c) => ({ ...c, printer_name: e.target.value }))}
                    placeholder="Xprinter XP-TT426B"
                    className="h-8 text-xs"
                    data-testid="bc-bridge-printer-name"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/print-bridge/download`}
                  download
                  className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline flex items-center gap-1"
                  data-testid="bc-bridge-download-exe"
                  title="Download the latest dyynit-print-bridge.exe and copy it to the shop PC"
                >
                  <Download className="w-3 h-3" /> Download bridge.exe
                </a>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={probeBridge} className="h-7 text-xs" data-testid="bc-bridge-recheck">
                    Re-check
                  </Button>
                  <Button size="sm" onClick={saveBridgeConfig} disabled={savingBridgeConfig} className="h-7 text-xs" data-testid="bc-bridge-save">
                    <Save className="w-3 h-3 mr-1" /> {savingBridgeConfig ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="px-6 py-3 flex items-center gap-2 flex-wrap">
            {/* Bridge status chip */}
            {bridgeStatus.ok ? (
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700 flex items-center gap-1"
                data-testid="bc-bridge-status"
                title={`Version: ${bridgeStatus.info?.version || 'unknown'}`}
              >
                <Plug className="w-3 h-3" /> Bridge v{bridgeStatus.info?.version || '?'}
                {bridgeStatus.info?.bartender_present && bridgeStatus.info?.btw_present ? "" : " (config needed)"}
              </span>
            ) : (
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border bg-amber-50 border-amber-200 text-amber-700 flex items-center gap-1"
                data-testid="bc-bridge-status"
              >
                <AlertCircle className="w-3 h-3" /> Bridge offline
              </span>
            )}
            <Button
              variant="ghost" size="sm"
              onClick={() => setBridgeConfigOpen((v) => !v)}
              data-testid="bc-bridge-config-toggle"
              title="Configure BarTender exe path, .btw template, printer name"
              className="h-7 text-xs"
            >
              <Wrench className="w-3.5 h-3.5 mr-1" /> Bridge
            </Button>

            <Button
              variant="ghost"
              onClick={handleTestPrint}
              disabled={printing}
              data-testid="bc-test-print-btn"
              title="Print 5 corner positions (TL/TR/C/BL/BR) — verify alignment without wasting product labels"
            >
              <Crosshair className="w-4 h-4 mr-1.5" />
              Test Print
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={printing} data-testid="bc-cancel-btn">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button onClick={handlePrint} disabled={totalLabels === 0 || printing} data-testid="bc-print-btn">
                <Printer className="w-4 h-4 mr-1.5" />
                {printing
                  ? "Sending…"
                  : totalLabels === 0
                    ? "Print"
                    : `Print ${totalLabels} Label${totalLabels === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Single stat cell used inside the Print Summary card. */
function SummaryCell({ label, value, testid, emphasis, muted }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span
        className={`font-mono-data mt-0.5 ${
          emphasis
            ? "text-xl font-semibold text-foreground"
            : muted
              ? "text-base text-muted-foreground"
              : "text-base font-medium text-foreground"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
